import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useUnreadCount } from '../Components/Hooks/NotificationHooks';
import { getLoggedinUser } from '../helpers/api_helper';
import config from '../config';
import webSocketService from '../services/websocket.service';

interface NotificationContextType {
  unreadCount: number;
  unreadMessageCount: number;
  totalUnreadCount: number;
  refreshCount: () => void;
  isOnline: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [processedMessageIds] = useState(new Set<number>());
  const currentUser = getLoggedinUser();
  
  // Extract user ID from nested structure
  const userId = currentUser?.user?.id || currentUser?.data?.user?.id || currentUser?.id;
  
  // Use the unread count hook with auto-refresh
  const { unreadCount, refresh } = useUnreadCount(currentUser?.id, true);
  
  // Fetch unread message count
  const fetchMessageCount = useCallback(async () => {
    const token = currentUser?.token || currentUser?.data?.token;
    const userId = currentUser?.user?.id || currentUser?.data?.user?.id || currentUser?.id;
    
    if (!userId || !token) return;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/user/unread-counts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUnreadMessageCount(data.data.totalUnread || 0);
        }
      }
    } catch (err) {
      console.error('Failed to fetch unread message count:', err);
    }
  }, [currentUser]);
  
  // Fetch message count initially and when refreshing
  useEffect(() => {
    fetchMessageCount();
  }, [fetchMessageCount]);
  
  // Auto-refresh message count every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMessageCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchMessageCount]);

  // Listen for WebSocket events for messages
  useEffect(() => {
    if (!userId) return;

    // Connect WebSocket if not already connected
    webSocketService.connect();

    const handleNewMessage = (message: any) => {
      // Prevent duplicate processing
      if (processedMessageIds.has(message.id)) {
        return;
      }
      
      processedMessageIds.add(message.id);
      
      // If the message is not from the current user, increment unread count
      if (message.sender_id !== userId) {
        setUnreadMessageCount(prev => prev + 1);
        
        // Show notification
        toast.info(`Neue Nachricht von ${message.sender_name}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    };

    const handleBadgeUpdate = (data: { totalUnread: number }) => {
      setUnreadMessageCount(data.totalUnread);
    };

    // Listen to all groups for new messages
    webSocketService.onMessage('*', handleNewMessage);

    // Wait a bit for socket to be initialized
    const setupSocketListeners = () => {
      const socket = (webSocketService as any).socket;
      if (socket && socket.connected) {
        socket.on('badge_update', handleBadgeUpdate);
      } else {
        setTimeout(setupSocketListeners, 1000);
      }
    };

    setupSocketListeners();

    return () => {
      webSocketService.offMessage('*', handleNewMessage);
      const socket = (webSocketService as any).socket;
      if (socket) {
        socket.off('badge_update', handleBadgeUpdate);
      }
    };
  }, [userId, processedMessageIds]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Refresh notifications when coming back online
      refresh();
      fetchMessageCount();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are currently offline. Notifications may not be up to date.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refresh, fetchMessageCount]);

  // Show notification when unread count increases
  const [previousUnreadCount, setPreviousUnreadCount] = useState(unreadCount);
  useEffect(() => {
    if (unreadCount > previousUnreadCount && previousUnreadCount > 0) {
      const newNotifications = unreadCount - previousUnreadCount;
      toast.info(
        `You have ${newNotifications} new notification${newNotifications > 1 ? 's' : ''}`, 
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    }
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount]);

  // Refresh notifications on window focus (user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      refresh();
      fetchMessageCount();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh, fetchMessageCount]);

  // Calculate total unread count
  const totalUnreadCount = unreadCount + unreadMessageCount;

  const value = {
    unreadCount,
    unreadMessageCount,
    totalUnreadCount,
    refreshCount: () => {
      refresh();
      fetchMessageCount();
    },
    isOnline
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};